import React, { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import {
  Archive, Bell, Bookmark, ChevronRight, Compass, FileText, Heart, Info, LogIn, LogOut,
  MessageCircle, MessageSquareHeart, Orbit, RadioTower, Scale, Search, Settings,
  ShieldCheck, UserPlus, UserRound, UserRoundX, VenetianMask, X,
} from "lucide-react";
import { fetchNotificationSummary } from "../api/notifications";
import { logoutAllDevices, type User } from "../api/profile";
import LogoutModal from "./LogoutModal";
import SupportDialog from "./SupportDialog";
import { BlackboxDialog } from "./HomePage";
import { requireIdentity } from "../utils/identityGate";
import "./ExploreDrawer.css";

type ExploreDrawerProps = {
  id: string;
  isOpen: boolean;
  onClose: () => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
  footerRef: RefObject<HTMLElement | null>;
  isAnonymous: boolean;
  profile?: User | null;
  fallbackUsername?: string;
};

type DrawerLink = {
  label: string;
  path: string;
  icon: React.ElementType;
  tone: "cyan" | "violet" | "amber";
};

const exploreLinks: DrawerLink[] = [
  { label: "Search", path: "/search", icon: Search, tone: "cyan" },
  { label: "Discover", path: "/discover", icon: Compass, tone: "violet" },
  { label: "Pulse", path: "/pulse", icon: RadioTower, tone: "violet" },
  { label: "Archive", path: "/archive", icon: Archive, tone: "amber" },
];

const identityLinks: DrawerLink[] = [
  { label: "Spaces", path: "/spaces", icon: Orbit, tone: "cyan" },
  { label: "Messages", path: "/message", icon: MessageCircle, tone: "violet" },
  { label: "Saved", path: "/saved", icon: Bookmark, tone: "amber" },
  { label: "Alerts", path: "/alerts", icon: Bell, tone: "cyan" },
];

const guestLinks: DrawerLink[] = [
  { label: "Spaces", path: "/spaces/all", icon: Orbit, tone: "cyan" },
];

function formatCount(value: number | null) {
  if (value === null) return "—";
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(value >= 10_000 ? 0 : 1)}K`;
  return String(value);
}

function startAnonymousBrowsing() {
  // Drop credentials and verified session caches, never account data or appearance preferences.
  const sessionKeys = [
    "token", "user", "blackspace_guest_id", "blackspace.home.feedSnapshot",
    "blackspace.home.surfaceSnapshot.v1", "blackspace.home.commentReturnAnchor",
    "blackspace.messages.snapshot.v1",
  ];
  for (const storage of [localStorage, sessionStorage]) {
    for (const key of sessionKeys) storage.removeItem(key);
  }
  // A full navigation also discards authenticated in-memory feed and socket state.
  window.location.replace(`/home?anonymous=${Date.now()}`);
}

export default function ExploreDrawer({
  id, isOpen, onClose, triggerRef, footerRef, isAnonymous, profile, fallbackUsername,
}: ExploreDrawerProps) {
  const location = useLocation();
  const drawerRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const onCloseRef = useRef(onClose);
  const restoreFocusRef = useRef(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [anonymousLogout, setAnonymousLogout] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportNotice, setSupportNotice] = useState(false);
  const supportOpenRef = useRef(false);
  const [layout, setLayout] = useState({ desktopWidth: 376, footerHeight: 68 });
  onCloseRef.current = onClose;
  supportOpenRef.current = supportOpen;

  useEffect(() => {
    if (!supportNotice) return;
    const timer = window.setTimeout(() => setSupportNotice(false), 8000);
    return () => window.clearTimeout(timer);
  }, [supportNotice]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const footer = footerRef.current?.querySelector<HTMLElement>(".bs-footer-nav");
    const updateLayout = () => {
      const triggerRight = triggerRef.current?.getBoundingClientRect().right ?? 0;
      const desktopWidth = Math.round(Math.min(window.innerWidth - 80, Math.max(376, triggerRight + 20)));
      const footerHeight = Math.round(footer?.getBoundingClientRect().height || 68);
      setLayout((current) => current.desktopWidth === desktopWidth && current.footerHeight === footerHeight
        ? current
        : { desktopWidth, footerHeight });
    };
    updateLayout();
    const layoutFrame = window.requestAnimationFrame(updateLayout);
    window.addEventListener("resize", updateLayout);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateLayout);
    if (footer) observer?.observe(footer);
    return () => {
      window.cancelAnimationFrame(layoutFrame);
      window.removeEventListener("resize", updateLayout);
      observer?.disconnect();
    };
  }, [footerRef, isOpen, triggerRef]);

  useEffect(() => {
    if (!isOpen || isAnonymous) return;
    let cancelled = false;
    fetchNotificationSummary()
      .then((summary) => { if (!cancelled) setUnreadCount(summary.unreadCount); })
      .catch(() => { if (!cancelled) setUnreadCount(0); });
    return () => { cancelled = true; };
  }, [isAnonymous, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    restoreFocusRef.current = true;
    const triggerElement = triggerRef.current;
    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => closeButtonRef.current?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (supportOpenRef.current) return;
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(drawerRef.current?.querySelectorAll<HTMLElement>("a[href], button:not([disabled])") || [])
        .filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const focusOutside = !drawerRef.current?.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || focusOutside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || focusOutside)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = priorOverflow;
      if (restoreFocusRef.current && triggerElement?.isConnected) triggerElement.focus();
    };
  }, [isOpen, triggerRef]);

  const closeForNavigation = () => {
    restoreFocusRef.current = false;
    onCloseRef.current();
  };

  const openLogout = () => {
    setAnonymousLogout(false);
    restoreFocusRef.current = false;
    onCloseRef.current();
    setLogoutOpen(true);
  };

  const handleLogout = async (allDevices: boolean) => {
    if (anonymousLogout) {
      startAnonymousBrowsing();
      return;
    }
    try {
      if (allDevices) await logoutAllDevices();
    } finally {
      const theme = localStorage.getItem("theme");
      localStorage.clear();
      sessionStorage.clear();
      if (theme) localStorage.setItem("theme", theme);
      window.location.replace("/login");
    }
  };

  const username = String(profile?.username || fallbackUsername || "").trim().replace(/^@+/, "");
  const profilePath = username ? `/user/${encodeURIComponent(username)}` : "";
  const name = String(profile?.displayName || username || "Your identity");
  const initial = name.slice(0, 1).toUpperCase();
  const isPresence = Boolean(profile?.isPresence || profile?.realm?.isPresence || profile?.realmRole === "presence" || profile?.role === "presence");
  const followersCount = typeof profile?.followersCount === "number" && Number.isFinite(profile.followersCount)
    ? Math.max(0, Math.floor(profile.followersCount)) : null;
  const followingCount = typeof profile?.followingCount === "number" && Number.isFinite(profile.followingCount)
    ? Math.max(0, Math.floor(profile.followingCount)) : null;

  const renderLinks = (links: DrawerLink[]) => links.map(({ label, path, icon: Icon, tone }) => {
    const active = location.pathname === path || (path === "/spaces/all" && location.pathname.startsWith("/spaces"));
    return (
      <Link
        key={path}
        to={path}
        onClick={closeForNavigation}
        className={`bs-explore-drawer-row bs-explore-drawer-tone-${tone}`}
        aria-current={active ? "page" : undefined}
      >
        <span className="bs-explore-drawer-row-icon"><Icon size={18} strokeWidth={2.1} aria-hidden="true" /></span>
        <span className="bs-explore-drawer-row-label">{label}</span>
        {label === "Alerts" && unreadCount > 0 && <span className="bs-explore-drawer-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>}
        <ChevronRight size={16} className="bs-explore-drawer-chevron" aria-hidden="true" />
      </Link>
    );
  });

  const desktopLink = (label: string, path: string, Icon: React.ElementType, needsIdentity = false) => (
    <Link
      to={path}
      className="bs-explore-desktop-row"
      onClick={(event) => {
        closeForNavigation();
        if (needsIdentity && isAnonymous) {
          event.preventDefault();
          requireIdentity(label.toLowerCase(), path);
        }
      }}
    >
      <Icon size={16} strokeWidth={1.8} aria-hidden="true" />
      <span>{label}</span>
    </Link>
  );

  const drawer = (
    <>
      <button
        type="button"
        className={`bs-explore-drawer-backdrop ${isOpen ? "is-open" : ""}`}
        onClick={() => onCloseRef.current()}
        aria-label="Close Explore menu"
        tabIndex={isOpen ? 0 : -1}
      />
      <aside
        id={id}
        ref={drawerRef}
        className={`bs-explore-drawer ${isOpen ? "is-open" : ""}`}
        style={{
          "--bs-explore-desktop-width": `${layout.desktopWidth}px`,
          "--bs-explore-footer-height": `${layout.footerHeight}px`,
        } as React.CSSProperties}
        role="dialog"
        aria-modal={isOpen && !supportOpen}
        aria-hidden={!isOpen || supportOpen}
        inert={supportOpen}
        aria-label={isAnonymous ? "Anonymous navigation" : "Blackspace navigation"}
      >
        <header className="bs-explore-drawer-header">
          {isAnonymous ? (
            <div className="bs-explore-drawer-profile">
              <span className="bs-explore-drawer-mark"><UserRound size={21} strokeWidth={1.8} aria-hidden="true" /></span>
              <span className="bs-explore-drawer-heading"><strong>Browsing anonymously</strong><small>Explore without an identity</small></span>
            </div>
          ) : profilePath ? (
            <Link to={profilePath} onClick={closeForNavigation} className="bs-explore-drawer-profile" aria-label="Open your profile">
              <span className="bs-explore-drawer-mark">
                {profile?.avatarUrl ? <img src={profile.avatarUrl} alt="" /> : initial}
              </span>
              <span className="bs-explore-drawer-heading"><strong>{name}</strong><small>@{username}</small></span>
            </Link>
          ) : (
            <div className="bs-explore-drawer-profile">
              <span className="bs-explore-drawer-mark">{initial}</span>
              <span className="bs-explore-drawer-heading"><strong>{name}</strong><small>Loading profile</small></span>
            </div>
          )}
          <button ref={closeButtonRef} type="button" className="bs-explore-drawer-close" onClick={() => onCloseRef.current()} aria-label="Close navigation menu" aria-expanded={isOpen} aria-controls={id}><X size={19} /></button>
        </header>

        {supportNotice && <div className="bs-explore-desktop-notice" role="status"><strong>Blackspace has noted it.</strong> Help will arrive soon.</div>}

        <nav className="bs-explore-desktop-nav" aria-label="Account navigation">
          <section className="bs-explore-desktop-section" aria-labelledby={`${id}-identity`}>
            <h2 id={`${id}-identity`}>Your identity</h2>
            {desktopLink("Posts", profilePath ? profilePath + "?tab=posts" : "/settings", FileText, true)}
            {desktopLink("Comments", "/activity/comments", MessageCircle, true)}
            {desktopLink("Emotions", "/activity/emotions", Heart, true)}
            {desktopLink("Archive", "/saved", Archive, true)}
          </section>
          <section className="bs-explore-desktop-section" aria-labelledby={`${id}-privacy`}>
            <h2 id={`${id}-privacy`}>Privacy & control</h2>
            <button type="button" className="bs-explore-desktop-row" onClick={() => {
              if (isAnonymous) { closeForNavigation(); return; }
              setAnonymousLogout(true);
              restoreFocusRef.current = false;
              onCloseRef.current();
              setLogoutOpen(true);
            }}><VenetianMask size={16} strokeWidth={1.8} aria-hidden="true" /><span>Anonymous Browsing</span></button>
            {desktopLink("Privacy & Safety", "/settings#settings-privacy", ShieldCheck, true)}
            {desktopLink("Blocked", "/message?tab=blocked", UserRoundX, true)}
          </section>
          <section className="bs-explore-desktop-section" aria-labelledby={`${id}-blackspace`}>
            <h2 id={`${id}-blackspace`}>Blackspace</h2>
            {desktopLink("Community Principles", "/terms", Scale)}
            <button type="button" className="bs-explore-desktop-row" onClick={() => { setSupportNotice(false); setSupportOpen(true); }}><MessageSquareHeart size={16} strokeWidth={1.8} aria-hidden="true" /><span>Feedback & Support</span></button>
            {desktopLink("About Blackspace", "/manifesto", Info)}
          </section>
        </nav>

        <div className="bs-explore-drawer-scroll bs-explore-drawer-mobile">
          {!isAnonymous && profile && !isPresence && profilePath && (
            <Link to={profilePath} onClick={closeForNavigation} className="bs-explore-drawer-network" title="View your profile">
              <span className="bs-explore-drawer-network-stat"><strong>{formatCount(followersCount)}</strong><small>Followers</small></span>
              <span className="bs-explore-drawer-network-divider" aria-hidden="true" />
              <span className="bs-explore-drawer-network-stat"><strong>{formatCount(followingCount)}</strong><small>Following</small></span>
            </Link>
          )}
          <section className="bs-explore-drawer-section" aria-label="Find and explore">
            <h2>Find and explore</h2>
            <div className="bs-explore-drawer-row-group">{renderLinks(exploreLinks)}</div>
          </section>
          <section className="bs-explore-drawer-section" aria-label={isAnonymous ? "Open spaces" : "Your space"}>
            <h2>{isAnonymous ? "Open spaces" : "Your space"}</h2>
            <div className="bs-explore-drawer-row-group">{renderLinks(isAnonymous ? guestLinks : identityLinks)}</div>
          </section>
        </div>

        <footer className="bs-explore-drawer-footer bs-explore-drawer-mobile">
          {isAnonymous ? (
            <div className="bs-explore-drawer-auth">
              <Link to="/login?mode=login" onClick={closeForNavigation}><LogIn size={17} aria-hidden="true" /> Sign in</Link>
              <Link to="/login?mode=signup" onClick={closeForNavigation}><UserPlus size={17} aria-hidden="true" /> Create identity</Link>
            </div>
          ) : (
            <div className="bs-explore-drawer-account-actions">
              <Link to="/settings" onClick={closeForNavigation} className="bs-explore-drawer-settings" aria-label="Settings" title="Settings"><Settings size={19} aria-hidden="true" /></Link>
              <button type="button" onClick={openLogout} className="bs-explore-drawer-signout" aria-label="Sign out" title="Sign out"><LogOut size={19} aria-hidden="true" /></button>
            </div>
          )}
          <span className="bs-explore-drawer-signoff">A quieter internet</span>
        </footer>

        <footer className="bs-explore-desktop-footer">
          <div className="bs-explore-desktop-account-actions">
            {isAnonymous ? <>
              <Link to="/login?mode=login" onClick={closeForNavigation}><LogIn size={16} aria-hidden="true" />Sign in</Link>
              <Link to="/login?mode=signup" onClick={closeForNavigation}><UserPlus size={16} aria-hidden="true" />Create identity</Link>
            </> : <>
              <Link to="/settings" onClick={closeForNavigation}><Settings size={16} strokeWidth={1.8} aria-hidden="true" />Settings</Link>
              <button type="button" className="bs-explore-desktop-signout" onClick={openLogout}><LogOut size={16} strokeWidth={1.8} aria-hidden="true" />Sign out</button>
            </>}
          </div>
          <span className="bs-explore-desktop-signoff">A quieter internet.</span>
        </footer>
      </aside>
      {supportOpen && (isAnonymous
        ? <BlackboxDialog open onClose={() => setSupportOpen(false)} />
        : <SupportDialog open onClose={() => setSupportOpen(false)} username={username} onCreated={() => { setSupportOpen(false); setSupportNotice(true); }} />)}
      <LogoutModal
        isOpen={logoutOpen}
        anonymousBrowsing={anonymousLogout}
        onConfirm={handleLogout}
        onCancel={() => { setLogoutOpen(false); triggerRef.current?.focus(); }}
      />
    </>
  );

  return typeof document === "undefined" ? drawer : createPortal(drawer, document.body);
}
